import React, { useState, useEffect, memo, useMemo } from 'react';
import { useLoaderData, useNavigate, useNavigation, useParams, Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { COMMIT } from '../utils/constants';
import { getBasicChartOptions } from './CommitDetailsGraph';
import { ProfilePane } from './FlameGraph.tsx';
import { extractTimeSeriesData, DataPoint } from '../utils/flamegraphLauncher.ts';
import ProfilerIcon from '../Profiler.ico';
import '../styles/Component.css';
import { useConfig } from "../configContext";
import type { Environment, Process, Service } from '../interfaces/Service';

// ===== Types =====

type NavState = {
    env: number;
    metricType: number;
    process: number;
    startDate: Date | null;
    endDate: Date | null;
};

type Filters = {
    env: number;
    metric: number;
    process: number;
    startDate: Date | string;
    endDate: Date | string;
};

type SelectOption = {
    key: number;
    text: string;
};

type FilterBarProps = {
    filters: Filters;
    onFilterChange: (key: string, value: number | string) => void;
    serviceName: string | undefined;
};

type FilterSelectProps = {
    label: string;
    value: number;
    onChange: (value: string) => void;
    options: SelectOption[];
};

type ChartGridProps = {
    components: any[];
    env: number;
    process: number;
    metric: number;
    onCardClick: (commit: any) => void;
};

type ChartCardProps = {
    commit: any;
    envs: number[];
    process: number;
    metric: number;
    onClick: (commit: any) => void;
};

// ===== Custom Hooks =====

function useFilters(navState: NavState): [Filters, React.Dispatch<React.SetStateAction<Filters>>] {
    const [filters, setFilters] = useState<Filters>(() => {
        const now = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(now.getMonth() - 1);

        return {
            env: Number(navState.env),
            metric: Number(navState.metricType),
            process: Number(navState.process),
            startDate: navState.startDate ?? oneMonthAgo,
            endDate: navState.endDate ?? now,
        };
    });

    useEffect(() => {
        Object.entries(filters).forEach(([k, v]) => {
            if (['env', 'metric', 'process'].includes(k)) {
                localStorage.setItem(k, String(v));
            }
        });
    }, [filters]);

    return [filters, setFilters];
}

// ===== Utilities =====

function filtersToQueryString(qp: Record<string, any>): string {
    const qs = new URLSearchParams();
    qs.set('env', qp.env);
    qs.set('metricType', qp.metricType);
    qs.set('process', qp.process);
    if (qp.startDate) qs.set('startDate', qp.startDate);
    if (qp.endDate) qs.set('endDate', qp.endDate);
    return qs.toString();
}

export function getComponentGraphData(details: any, env: number, metricType: number, process: number): any[] {
    return details.timeSeries.filter((m: any) => m.metricType === metricType && m.env === env && m.process === process);
}

export function getDatasetFromComponentTimeSeries(timeSeries: any[], functionId: number | null = null) {
    const data: { x: Date; y: number; originalDate: string }[] = [];
    for (const p of timeSeries) {
        const day = (new Date(p.profileDate)).getDay();
        if (day === 0 || day === 6) continue; // skip weekends
        data.push({
            x: new Date(p.profileDate),
            y: p.metricValue,
            originalDate: p.profileDate.substring(0, 10) + (functionId != null ? '/' + functionId : ''),
        });
    }
    return { data };
}

function formatDate(date: Date | string | null): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
}

// ===== Main Page Component =====

export function ComponentView() {
    const { serviceName } = useParams<{ serviceName: string }>();
    const { components, navState } = useLoaderData() as { components: any[]; navState: NavState };
    const navigate = useNavigate();
    const navigation = useNavigation();
    const config = useConfig();
    const [filters, setFilters] = useFilters(navState);
    const [selectedCommit, setSelectedCommit] = useState<any | null>(null);
    const isLoading = navigation.state === 'loading';

    const service = config.services.find((candidate: Service) => candidate.name === serviceName);
    const serviceEnvironments: Environment[] = service?.Environments ?? Object.values(service?.environments ?? {});
    const environmentIds: number[] = serviceEnvironments.map((environment) => environment.id);
    const highestEnvId = Math.max(...environmentIds, 0);
    const isAllEnvs = filters.env > highestEnvId;
    const panelEnv = isAllEnvs ? Math.min(...environmentIds) : filters.env;

    useEffect(() => { document.title = 'PR Guardian - Components'; }, []);

    const handleFilterChange = (key: string, value: number | string) => {
        setFilters(prev => {
            const updated = { ...prev, [key]: value };
            const nav = { ...navState, ...updated, metricType: updated.metric };
            navigate(`/service/${serviceName}/components?` + filtersToQueryString(nav));
            return updated;
        });
    };

    return (
        <div className="page-container">
            <FilterBar filters={filters} onFilterChange={handleFilterChange} serviceName={serviceName} />
            {isLoading ? (
                <LoadingState />
            ) : (
                <ChartGrid
                    components={components}
                    onCardClick={setSelectedCommit}
                    {...filters}
                />
            )}

            <Panel
                className="component-panel"
                isLightDismiss
                isOpen={selectedCommit != null}
                type={PanelType.medium}
                onDismiss={() => setSelectedCommit(null)}
                closeButtonAriaLabel="Close"
                headerText={selectedCommit?.commitId?.replace('Component-', '')}
            >
                {selectedCommit && (
                    <div className="component-panel-content">
                        <Link
                            className="component-panel-link"
                            to={`/service/${serviceName}/ComponentDetails/${selectedCommit.commitId}?env=${panelEnv}&process=${filters.process}&metricType=${filters.metric}&startDate=${new Date(filters.startDate).toISOString()}&endDate=${new Date(filters.endDate).toISOString()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open full details page &#8599;
                        </Link>
                        <div className="component-panel-graph">
                            <PanelChart
                                commit={selectedCommit}
                                envs={isAllEnvs ? environmentIds : [filters.env]}
                                process={filters.process}
                                metric={filters.metric}
                                serviceId={service?.id ?? 0}
                            />
                        </div>
                    </div>
                )}
            </Panel>
        </div>
    );
}

// ===== Loading State =====

function LoadingState() {
    return (
        <div className="component-loading">
            <div className="component-spinner" />
            Loading components...
        </div>
    );
}

// ===== Filter Bar =====

function FilterBar({ filters, onFilterChange, serviceName }: FilterBarProps) {
    const config = useConfig();
    const service = config.services.find((candidate: Service) => candidate.name === serviceName);
    const serviceEnvironments: Environment[] = service?.Environments ?? Object.values(service?.environments ?? {});
    const environmentIds: number[] = serviceEnvironments.map((environment) => environment.id);
    const processes: Process[] = service?.Processes ?? Object.values(service?.processes ?? {});
    const selectedProcess = processes.find((process) => process.id === filters.process);
    const availableMetrics = selectedProcess ? Object.values(selectedProcess.metrics) : [];
    const highestEnvId = Math.max(...environmentIds, 0);

    return (
        <div className="component-filters">
            <FilterSelect
                label="Environment:"
                value={filters.env}
                onChange={v => onFilterChange('env', Number(v))}
                options={[
                    { key: highestEnvId + 1, text: "All Environments" },
                    ...serviceEnvironments.map((environment) => ({ key: environment.id, text: environment.name }))
                ]}
            />
            <FilterSelect
                label="Process:"
                value={filters.process}
                onChange={v => onFilterChange('process', Number(v))}
                options={processes.map((process) => ({ key: process.id, text: process.name }))}
            />
            <FilterSelect
                label="Metric:"
                value={filters.metric}
                onChange={v => onFilterChange('metric', Number(v))}
                options={availableMetrics.map((metricOption) => ({ key: metricOption.id, text: metricOption.name }))}
            />
            <div className="filter-item">
                <label className="filter-label">Start Date:</label>
                <input
                    type="date"
                    className="filter-date"
                    value={formatDate(filters.startDate)}
                    onChange={e => onFilterChange('startDate', e.target.value)}
                />
            </div>
            <div className="filter-item">
                <label className="filter-label">End Date:</label>
                <input
                    type="date"
                    className="filter-date"
                    value={formatDate(filters.endDate)}
                    onChange={e => onFilterChange('endDate', e.target.value)}
                />
            </div>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
    return (
        <div className="filter-item">
            <label className="filter-label">{label}</label>
            <select className="filter-select" value={value} onChange={e => onChange(e.target.value)}>
                {options.map(o => <option key={o.key} value={o.key}>{o.text}</option>)}
            </select>
        </div>
    );
}

// ===== Chart Grid =====

function ChartGrid({ components, env, process, metric, onCardClick }: ChartGridProps) {
    const config = useConfig();
    const serviceName = useParams<{ serviceName: string }>().serviceName;
    const service = config.services.find((candidate: Service) => candidate.name === serviceName);
    const serviceEnvironments: Environment[] = service?.Environments ?? Object.values(service?.environments ?? {});
    const environmentIds: number[] = useMemo(
        () => serviceEnvironments.map((environment) => environment.id),
        [serviceEnvironments]
    );
    const highestEnvId = Math.max(...environmentIds, 0);
    const envs = useMemo(
        () => env <= highestEnvId ? [env] : [...environmentIds],
        [env, highestEnvId, environmentIds]
    );

    return (
        <div className="chart-grid">
            {components.map(commit => (
                <ChartCard
                    key={commit.commitId}
                    commit={commit}
                    envs={envs}
                    process={process}
                    metric={metric}
                    onClick={onCardClick}
                />
            ))}
        </div>
    );
}

// ===== Chart Card (pure display — no Panel, no service lookup) =====

const ChartCard = memo(function ChartCard({ commit, envs, process, metric, onClick }: ChartCardProps) {
    const { data, options } = useMemo(() => {
        const datasets = envs.map(targetEnv => {
            const details = getComponentGraphData(commit, targetEnv, metric, process);
            const ds = getDatasetFromComponentTimeSeries(details);
            (ds as any).label = '';
            return ds;
        });

        const opts: any = getBasicChartOptions(metric, COMMIT, true);
        opts.scales.x.title.text = '';
        opts.scales.x.ticks = { display: true, maxTicksLimit: 4, font: { size: 9 }, color: '#999' };
        opts.scales.y.title.text = '';
        opts.scales.y.beginAtZero = false;
        opts.elements = { point: { pointStyle: false } };
        opts.animation = false;
        opts.responsive = true;
        opts.maintainAspectRatio = true;
        opts.aspectRatio = 2;
        opts.resizeDelay = 100;
        opts.plugins.tooltip.position = 'nearest';
        opts.plugins.tooltip.intersect = false;
        opts.plugins.tooltip.mode = 'nearest';
        opts.plugins.tooltip.callbacks.label = (ctx: any) => {
            const label = ctx.dataset.label || '';
            const value = typeof ctx.parsed?.y === 'number' ? ctx.parsed.y.toFixed(3) : ctx.formattedValue;
            return label ? `${label}: ${value}` : value;
        };

        return { data: { datasets }, options: opts };
    }, [commit, envs, process, metric]);

    const handleClick = () => onClick(commit);
    options.onClick = handleClick;

    const componentName = commit.commitId.replace('Component-', '');

    return (
        <div className="chart-card" onClick={handleClick}>
            <div className="chart-card-header">{componentName}</div>
            <div className="chart-card-body">
                <Line data={data} options={options} />
            </div>
        </div>
    );
});

// ===== Panel Chart (per-environment stacked charts with click-to-profile) =====

type PanelChartProps = {
    commit: any;
    envs: number[];
    process: number;
    metric: number;
    serviceId: number;
};

function PanelChart({ commit, envs, process, metric, serviceId }: PanelChartProps) {
    const config = useConfig();
    const serviceName = useParams<{ serviceName: string }>().serviceName;
    const service = config.services.find((candidate: Service) => candidate.name === serviceName);
    const environments: Environment[] = service?.Environments ?? Object.values(service?.environments ?? {});
    const [profileData, setProfileData] = useState<{ envId: number; date: string; timeSeriesData?: DataPoint[] } | null>(null);
    const setProfileDate = (date: string | null) => {
        if (date) setProfileData(prev => ({ envId: prev?.envId ?? envs[0], date, timeSeriesData: prev?.timeSeriesData }));
        else setProfileData(null);
    };

    return (
        <div className="panel-chart-stack">
            <div className="panel-chart-hint">
                <img src={ProfilerIcon} alt="" className="panel-chart-hint-icon" />
                Click a data point to view flame graph
            </div>
            {envs.map(envId => {
                const envName = environments.find((environment) => environment.id === envId)?.name || `Env ${envId}`;
                const details = getComponentGraphData(commit, envId, metric, process);
                const ds = getDatasetFromComponentTimeSeries(details);
                (ds as any).label = envName;

                const handleChartClick = (_event: any, elements: any[], chart: any) => {
                    if (elements.length === 0) return;
                    const el = elements[0];
                    const datapoint = chart.data.datasets[el.datasetIndex].data[el.index];
                    if (datapoint?.originalDate) {
                        const timeSeriesData = extractTimeSeriesData(chart);
                        setProfileData({ envId, date: datapoint.originalDate, timeSeriesData });
                    }
                };

                // Pass setProfileDate so window.g_tooltipSelect is set (needed for ProfilePane dismiss)
                const opts: any = getBasicChartOptions(metric, COMMIT, true, setProfileDate);
                opts.scales.y.beginAtZero = false;
                opts.animation = false;
                opts.responsive = true;
                opts.maintainAspectRatio = true;
                opts.onClick = handleChartClick;
                opts.plugins.tooltip.callbacks.label = (ctx: any) => {
                    const label = ctx.dataset.label || '';
                    const value = typeof ctx.parsed?.y === 'number' ? ctx.parsed.y.toFixed(3) : ctx.formattedValue;
                    return `${label}: ${value}`;
                };

                return (
                    <div key={envId} className="panel-chart-item">
                        <div className="panel-chart-env-label">{envName}</div>
                        <Line data={{ datasets: [ds] }} options={opts} />
                    </div>
                );
            })}
            {profileData && (
                <ProfilePane
                    mode="commit"
                    serviceId={serviceId}
                    commitId={commit.commitId}
                    env={profileData.envId}
                    process={process}
                    metric={metric}
                    type={0}
                    date={profileData.date}
                    isCommitData={false}
                    timeSeriesData={profileData.timeSeriesData}
                    enableComparison={true}
                />
            )}
        </div>
    );
}
